import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ComputerConfig } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import { IcCheck, IcChevronLeft, IcChevronRight } from "@app/components/icons";
import { SpecText, btn, btnPrimary, btnSoft } from "@app/components/ui";

interface VpsCatalogProps {
  configs: ComputerConfig[];
  /** discount_vps của tài khoản */
  discount: number;
  selectedId: string;
  onPick: (config: ComputerConfig) => void;
}

/** Danh sách gói VPS (ComputerConfig): 2 hàng thẻ, nhiều gói thì trượt ngang bằng mũi tên. */
function VpsCatalog({ configs, discount, selectedId, onPick }: VpsCatalogProps) {
  const t = useT();
  const [edges, setEdges] = useState({ start: true, end: true });

  const gridRef = useRef<HTMLDivElement>(null);

  const updateEdges = () => {
    const g = gridRef.current;

    if (g) setEdges({ start: g.scrollLeft < 4, end: g.scrollLeft + g.clientWidth >= g.scrollWidth - 4 });
  };

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);

    return () => window.removeEventListener("resize", updateEdges);
  }, [configs.length]);

  const slide = (dir: number) => gridRef.current?.scrollBy({ left: dir * gridRef.current.clientWidth, behavior: "smooth" });

  const arrow =
    "absolute top-1/2 z-[3] grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-line bg-surface text-brand shadow-card hover:bg-brand hover:text-white";

  return (
    <section className="flex min-w-0 flex-col gap-3.5">
      <div className="text-[13px] text-muted">
        {t("Có")} <b className="text-fg">{configs.length}</b> {t("gói")}
      </div>

      {!configs.length ? (
        <p className="m-0 rounded-card border border-dashed border-line-2 px-4 py-10 text-center text-muted">
          {t("Chưa có gói VPS nào.")}
        </p>
      ) : (
        <div className="pk-wrap relative flex flex-1 flex-col">
          {!edges.start && (
            <button type="button" className={clsx(arrow, "-left-5 max-[760px]:-left-3")} onClick={() => slide(-1)}>
              <IcChevronLeft className="h-[18px] w-[18px]" />
            </button>
          )}
          <div
            ref={gridRef}
            onScroll={updateEdges}
            className="no-scrollbar -mx-[3px] -mb-2 -mt-1 grid flex-1 snap-x snap-mandatory gap-3 overflow-x-auto px-[3px] pb-2 pt-1 pk-grid pk-2row"
            // số cột = nửa số gói (làm tròn lên), hàng 1 là nửa đầu, hàng 2 là nửa sau
            style={{ ["--cols" as string]: Math.max(1, Math.ceil(configs.length / 2)) }}
          >
            {configs.map((c) => {
              const on = selectedId === c._id;
              const price = c.monthly_rental_price - (c.monthly_rental_price * discount) / 100;

              return (
                <article
                  key={c._id}
                  onClick={() => onPick(c)}
                  className={clsx(
                    "flex min-w-0 cursor-pointer snap-start flex-col gap-2.5 rounded-card border bg-surface p-3.5 transition-shadow hover:shadow-card",
                    on ? "border-brand shadow-[0_0_0_3px_var(--brand-soft)]" : "border-line"
                  )}
                >
                  <span
                    className={clsx(
                      "w-fit rounded-md px-[7px] py-px text-[11px] font-semibold",
                      c.gpu ? "bg-c-gm-soft text-c-gm" : "bg-surface-2 text-muted"
                    )}
                  >
                    {c.gpu ? t("Có GPU") : t("Không GPU")}
                  </span>
                  <h3 className="text-base font-bold">{c.config_name}</h3>
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <strong className="text-[22px] font-bold tabular-nums text-brand">{formatMoneyVN(price)}</strong>
                    <span className="text-xs text-muted">VND/{t("tháng")}</span>
                    {discount > 0 && <s className="text-xs text-faint">{formatMoneyVN(c.monthly_rental_price)}</s>}
                  </div>
                  <dl className="m-0 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2.5 gap-y-0.5 text-[12.5px]">
                    {(
                      [
                        ["CPU", c.cpu],
                        ["RAM", c.ram],
                        ["SSD", c.ssd],
                        ["GPU", c.gpu || t("Không")],
                        [t("Mạng"), c.network_speed],
                        ["IPv4", c.ipv4],
                        ["OS", c.os],
                        [t("Máy chủ"), c.data_center],
                      ] as [string, string][]
                    )
                      .filter(([, v]) => v)
                      .map(([k, v]) => (
                        <div key={k} className="contents">
                          <dt className="font-medium text-faint">{k}</dt>
                          <dd className={clsx("m-0 min-w-0 font-medium", k === "GPU" && c.gpu && "font-bold text-ok")}>
                            <SpecText value={v} />
                          </dd>
                        </div>
                      ))}
                  </dl>
                  <button
                    type="button"
                    className={clsx(btn, "mt-auto w-full", on ? btnSoft : btnPrimary)}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPick(c);
                    }}
                  >
                    {on && <IcCheck className="h-4 w-4" />}
                    {on ? t("Đã chọn") : t("Chọn gói")}
                  </button>
                </article>
              );
            })}
          </div>
          {!edges.end && (
            <button type="button" className={clsx(arrow, "-right-5 max-[760px]:-right-3")} onClick={() => slide(1)}>
              <IcChevronRight className="h-[18px] w-[18px]" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export default VpsCatalog;
